import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Smartphone, Loader2, CheckCircle, Copy, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const TOTPSetupModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verify Code, 3: Backup Codes
  const [isLoading, setIsLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      initiateSetup();
    }
  }, [isOpen]);

  const initiateSetup = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/settings/security/totp/setup`,
        {},
        { withCredentials: true }
      );
      setSetupData(response.data);
      setStep(1);
    } catch (error) {
      console.error('TOTP setup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate 2FA setup');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/settings/security/totp/verify`,
        {
          totp_code: verificationCode,
          secret: setupData.secret
        },
        { withCredentials: true }
      );
      
      setBackupCodes(response.data.backup_codes);
      setStep(3);
      toast.success('Two-factor authentication enabled successfully!');
    } catch (error) {
      console.error('TOTP verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadBackupCodes = () => {
    const text = `Payo Two-Factor Authentication Backup Codes
Generated: ${new Date().toLocaleDateString()}

${backupCodes.join('\n')}

IMPORTANT:
- Each code can only be used once
- Store these codes in a secure location
- You can regenerate codes from Account Settings`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'payo-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded!');
  };

  const handleComplete = () => {
    onSuccess();
    onClose();
    // Reset state
    setStep(1);
    setVerificationCode('');
    setBackupCodes([]);
    setSetupData(null);
  };

  const handleClose = () => {
    if (step === 3) {
      handleComplete();
    } else {
      onClose();
      // Reset state
      setStep(1);
      setVerificationCode('');
      setBackupCodes([]);
      setSetupData(null);
    }
  };

  if (isLoading && !setupData) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px]">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Setting up 2FA...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Smartphone className="w-5 h-5 text-blue-600" />
            <span>Enable Two-Factor Authentication</span>
          </DialogTitle>
          <DialogDescription>
            Add an extra layer of security to your account using an authenticator app.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6">
            {[
              { num: 1, label: 'Scan QR Code' },
              { num: 2, label: 'Verify Code' },
              { num: 3, label: 'Backup Codes' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s.num 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="text-xs text-gray-600 mt-1">{s.label}</span>
                </div>
                {idx < 2 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step > s.num ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: QR Code */}
          {step === 1 && setupData && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code with Google Authenticator or Authy
                </p>
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                  <img 
                    src={setupData.qr_code} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>

              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-600 mb-2 font-medium">
                  Can't scan the code? Enter this key manually:
                </p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 text-sm bg-white px-3 py-2 rounded border border-gray-300 font-mono">
                    {setupData.manual_entry_key}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.manual_entry_key)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setStep(2)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Next: Verify Code
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <Label htmlFor="verification_code">Enter 6-Digit Code</Label>
                <Input
                  id="verification_code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono mt-1"
                  maxLength={6}
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button 
                  type="submit"
                  disabled={isLoading || verificationCode.length !== 6}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Verify & Enable 2FA
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: Backup Codes */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">2FA Enabled Successfully!</span>
                </div>
                <p className="text-sm text-green-700">
                  Your account is now protected with two-factor authentication.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">Backup Codes</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadBackupCodes}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Save these backup codes in a secure location. Each code can be used once if you lose access to your authenticator app.
                </p>
                
                <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  {backupCodes.map((code, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 bg-white rounded border border-gray-300"
                    >
                      <code className="text-sm font-mono">{code}</code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(code)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Important:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Store these codes in a password manager or secure location</li>
                      <li>• Each code works only once</li>
                      <li>• You can regenerate codes anytime from Security Settings</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button 
                  type="button"
                  onClick={handleComplete}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TOTPSetupModal;
